import { List } from "@mui/icons-material";
import { Alert, Box } from "@mui/material";
import { GridSortDirection, GridSortModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CustomDataGrid, DeletePanel, Layout, MyPaper, TitleHeader } from "../../../components";
import { SelectBoxOption } from "../../../interfaces";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useDeletePricingMutation, useDuplicatePricingMutation, useGetCurrencyQuery, useLazyGetAllPricingQuery, useLazyGetBOMsQuery } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { openDialog } from "../../../redux/slices/dialog";
import { resetPricingSlice } from "../../../redux/slices/pricing";
import { HasAccess } from "../../../router/authorization";
import { PERMISSIONS } from "../../../router/permission";
import { AppRoutes } from "../../../router/routes";
import { searchLocalList } from "../../../utils";
import { getObjectType } from "../../bomReport/addEditBOMReport";
import { PublishComponent } from "../../materials/dashboard";
import { PricingListColumn } from "./utils";

const Dashboard: React.FC<{ children?: JSX.Element }> = (props) => {

    const { id: p_id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { perm } = useAppSelector(store => store.auth.userDetails);

    const currencyData = useGetCurrencyQuery(null);

    const dropDownOptions = [{ value: "", label: "All" }, ...(currencyData?.data ?? [])];

    const [getAllPricing, { data: allData = { data: [], totalCount: 0 }, isLoading, isError, isFetching }] = useLazyGetAllPricingQuery({});
    const { data, totalCount } = allData;
    const [getBOMs, bomRes] = useLazyGetBOMsQuery();

    const [duplicatePricing] = useDuplicatePricingMutation();
    const [deletePricing] = useDeletePricingMutation();

    const [search, setSearch] = React.useState("");
    const [dropDown, updateDropDown] = React.useState<SelectBoxOption>(dropDownOptions[0]);
    const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 100 });
    const [queryOptions, setQueryOptions] = React.useState({ sortModel: [{ field: 'sequence_id', sort: 'desc' as GridSortDirection }] });

    const creatable = React.useMemo(() => {
        let primaryAccess = HasAccess(perm, PERMISSIONS.COMMERCIAL_PRICING_CREATE);
        return primaryAccess;
        // eslint-disable-next-line
    }, [perm, location.pathname]);

    const deletable = React.useMemo(() => {
        let primaryAccess = HasAccess(perm, PERMISSIONS.COMMERCIAL_PRICING_DELETE);
        return primaryAccess;
        // eslint-disable-next-line
    }, [perm, location.pathname]);

    const publishable = React.useMemo(() => {
        let primaryAccess = HasAccess(perm, PERMISSIONS.COMMERCIAL_PRICING_PUBLISH);
        return primaryAccess;
        // eslint-disable-next-line
    }, [perm, location.pathname]);

    const viewPricing = (data: any, isEdit: boolean = true) => {
        dispatch(resetPricingSlice());
        navigate(AppRoutes.viewPricingBasicDetail(isEdit ? data._id : "New"))
    };

    const duplicate = (data: any) => {
        let message = `Do you want to create a duplicate of this "${data.sequence_id}" pricing?`;
        dispatch(
            openConfirmationDialog({
                title: "Are you sure?",
                body: message,
                positiveBtn: "Duplicate",
                negativeBtn: "Cancel",
                onOk: () => performDuplicate(data),
                onNegativeBtn: () => dispatch(closeConfirmationDialog()),
            })
        );
    };

    const performDuplicate = async (data: any) => {
        dispatch(openBackdrop(`Creating duplicate of "${data.sequence_id}" pricing...`));
        try {
            await duplicatePricing(data?._id).unwrap().then((res) => {
                if (res?._id) {
                    if (Object.keys(res).includes("message")) {
                        enqueueSnackbar(
                            `Created duplicate of ${res.sequence_id} Successfully!`,
                            { variant: "success" }
                        );
                        dispatch(closeConfirmationDialog());
                        dispatch(closeBackdrop());
                        refetch();
                    } else if (Object.keys(res).includes("error")) {
                        // eslint-disable-next-line
                        throw "Unable to create duplicate";
                    } else {
                        // eslint-disable-next-line
                        throw "Data not found";
                    }
                }
            })
        } catch (error) {
            let errorMessage: string = `Unable to create duplicate of ${data.sequence_id}!`;
            enqueueSnackbar(errorMessage, { variant: "error" });
            dispatch(closeBackdrop());
        } finally {
            dispatch(closeConfirmationDialog());
        }
    };

    const handleDeletePricing = (pricing: any) => {
        let message = `Are you sure you want to delete the Pricing ${pricing.sequence_id}?`;
        dispatch(openConfirmationDialog({
            title: "Delete Operation",
            body: <DeletePanel
                message={message}
                warningMessage={""}
            />,
            positiveBtn: "Delete",
            negativeBtn: "Cancel",
            onOk: () => performRowDel(pricing),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }))
    };

    const performRowDel = async (pricing: any) => {
        dispatch(openBackdrop("Deleting pricing..."));
        try {
            await deletePricing({ pricing_id: pricing?._id }).unwrap().then((res) => {
                enqueueSnackbar(`Deleted pricing Successfully!`, { variant: "success" })
                refetch();
            })
        } catch (error: any) {
            let errorMessage: string = error?.data?.title ?? "Oops! Something went wrong, Unable to delete"
            enqueueSnackbar(errorMessage, { variant: "error" });
        } finally {
            dispatch(closeConfirmationDialog())
            dispatch(closeBackdrop());
        }
    }

    const giveMeRows = () => {
        if (search.trim().length && data && !isLoading) {
            return data.filter((d: any) => searchLocalList(d, search));
        } else if (!isLoading && data) {
            return data ?? [];
        } else {
            return [];
        }
    };

    const getParentPath = (id: string) => AppRoutes.viewPricingBasicDetail(id);

    let ROWS = giveMeRows();

    const refetch = () => {
        getAllPricing({
            sort_by: queryOptions?.sortModel?.[0]?.field,
            sort_order: queryOptions?.sortModel?.[0]?.sort,
            object_id: p_id as string,
            object_type: getObjectType(location) as string,
            currency: dropDown?.value as string,
            ...paginationModel
        })
    }

    const updatePublishStatus = (row: any) => {
        dispatch(
            openConfirmationDialog({
                title: "Publish Operation",
                body: <DeletePanel
                    message={`Are you sure you want to publish the pricing ${row?.sequence_id}?`}
                />,
                positiveBtn: "Confirm",
                negativeBtn: "Cancel",
                onOk: () => proceedToPublish(row),
                onNegativeBtn: () => dispatch(closeConfirmationDialog())
            })
        );
    };

    const proceedToPublish = (row: any) => {
        dispatch(closeConfirmationDialog());
        dispatch(openDialog({
            title: "",
            body: <PublishComponent {...row} refetch={refetch} object_type="bomPricing" object_id={row?._id} />,
            hidePositiveBtn: true,
            hideNegativeBtn: true,
            enablePadding: false
        }));
    };

    const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
        // Here you save the data you need from the sort model
        setQueryOptions({ sortModel: [...sortModel] });
    }, [])

    React.useEffect(() => {
        refetch();
        // eslint-disable-next-line
    }, [dropDown, paginationModel, JSON.stringify(queryOptions?.sortModel)]);

    React.useEffect(() => {
        setSearch("");
    }, [paginationModel]);
    
    React.useEffect(() => {
        getBOMs({ page: null, pageSize: null, include_histories: true });
        // eslint-disable-next-line
    }, []);

    return (
        <MyPaper>
            <>
                {/* Header */}
                <TitleHeader
                    title="Pricing"
                    count={ROWS?.length ?? 0}
                    showSearch={true}
                    search={search}
                    onSearchChange={setSearch}
                    searchPlaceholder={"Search for Pricing"}
                    showCreateBtn={creatable}
                    createBtnLabel={"New Pricing"}
                    onCreateBtn={() => viewPricing(null, false)}
                    showDropDown={true}
                    onDropDownChange={(data: SelectBoxOption) => updateDropDown(data)}
                    dropDownLabel="Currency"
                    dropDownOptions={dropDownOptions}
                    dropDownValue={dropDown}
                    showFilterSelect={false}
                />

                {/* Error */}
                {isError && <Alert sx={{ mt: 2 }} severity="error">Oops! Something went wrong, Unable to fetch Materials. Try Again Later!</Alert>}

                {/* Data Grid */}
                <Box sx={{
                    mt: 2,
                    height: "calc(100vh - 216px)"
                }}>
                    <CustomDataGrid
                        id={`pricing-list`}
                        rows={ROWS}
                        columns={PricingListColumn({ onDelete: handleDeletePricing, view: viewPricing, updatePublishStatus, removeDelete: !deletable, publishable, getParentPath, handleDuplicate: duplicate, bomRes: bomRes?.data?.data ?? [] })}
                        loading={isFetching || isLoading}
                        getRowId={(row) => row._id}
                        showToolbar={true}
                        rowCount={totalCount}
                        paginationModel={paginationModel}
                        paginationMode="server"
                        onPaginationModelChange={setPaginationModel}
                        sortingMode="server"
                        onSortModelChange={handleSortModelChange}
                    />
                </Box>
            </>
        </MyPaper>
    );
}

export const PricingList: React.FC<{ children?: JSX.Element }> = (props) => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    React.useEffect(() => {
        dispatch(closeBackdrop());
        // eslint-disable-next-line
    }, [])

    return <Box sx={{ padding: 0.5, height: "100%" }}>
        {/* Breadscrum */}
        <Layout
            history={[
                { label: "Commercial Module", onClick: () => navigate(AppRoutes.pricingList) },
            ]}
            currentPath={"Pricing"}
            hideNavBarByDefault={true}
            navBars={[{
                id: "1",
                title: "Pricing",
                icon: <List fontSize="small" />,
                path: AppRoutes.allQuickQuotes
            },]}
            mainPanel={<Dashboard />}
            sideNavVariant={"whiteboard"}
        />
    </Box>
}